import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Auth = () => {
  const { loginWithPassword, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await loginWithPassword(email, password);
        toast.success("Welcome back.");
      } else {
        await register(email, password, name);
        toast.success("Account created. Welcome.");
      }
      navigate(location.state?.from || "/account");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="auth-page"
      className="min-h-[80vh] grid grid-cols-1 lg:grid-cols-2"
    >
      <div
        className="hidden lg:block relative"
        style={{
          backgroundImage:
            "url('https://static.prod-images.emergentagent.com/jobs/6b746f73-141d-4e23-a891-20657f4c6e33/images/e809a07e3103e1da470d21e6d553e04bc3fa58edd64c7c19e6c9da9bf83f0f0f.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute bottom-16 left-12 right-12">
          <div className="overline mb-4">A House for Adults</div>
          <div className="font-serif-display text-5xl text-bone leading-tight max-w-md">
            Sign in. Slip out of the everyday.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="overline mb-4">Ram Shack</div>
          <h1 className="font-serif-display text-4xl text-bone mb-2">
            {mode === "login" ? "Welcome back." : "Become a member."}
          </h1>
          <p className="text-muted-bone text-sm mb-10">
            {mode === "login"
              ? "Continue where you left off."
              : "Save your favourites, track orders, and unlock private drops."}
          </p>

          <button
            data-testid="auth-google-btn"
            onClick={loginWithGoogle}
            type="button"
            className="w-full px-8 py-4 uppercase text-xs tracking-[0.22em] text-bone border hover:bg-white/5 transition mb-4"
            style={{ borderColor: "rgba(245,242,235,0.25)" }}
          >
            Continue with Google
          </button>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px" style={{ background: "var(--rs-border)" }} />
            <span className="overline">Or with email</span>
            <div className="flex-1 h-px" style={{ background: "var(--rs-border)" }} />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {mode === "register" && (
              <input
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Your name"
                className="w-full px-4 py-3 bg-transparent border text-bone outline-none focus:border-[var(--rs-terracotta)]"
                style={{ borderColor: "var(--rs-border)" }}
              />
            )}
            <input
              data-testid="auth-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Email"
              className="w-full px-4 py-3 bg-transparent border text-bone outline-none focus:border-[var(--rs-terracotta)]"
              style={{ borderColor: "var(--rs-border)" }}
            />
            <input
              data-testid="auth-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              placeholder="Password"
              className="w-full px-4 py-3 bg-transparent border text-bone outline-none focus:border-[var(--rs-terracotta)]"
              style={{ borderColor: "var(--rs-border)" }}
            />
            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full px-8 py-4 uppercase text-xs tracking-[0.22em] text-bone hover:opacity-90 transition disabled:opacity-60"
              style={{ background: "var(--rs-oxblood)" }}
            >
              {loading
                ? "Working…"
                : mode === "login"
                  ? "Sign in"
                  : "Create account"}
            </button>
          </form>

          <button
            data-testid="auth-mode-toggle"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="text-xs uppercase tracking-[0.22em] text-muted-bone hover:text-bone mt-6 block"
          >
            {mode === "login"
              ? "New here? Create an account →"
              : "Already a member? Sign in →"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Auth;
